import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, Phone, CalendarCheck, ShieldCheck } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { COMPANY } from "@/lib/site-data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.22, 1, 0.36, 1];

const ROOF_TYPES = [
  { id: "tuile", label: "Tuile canal / romane", sub: "La toiture du Sud-Ouest" },
  { id: "ardoise", label: "Ardoise", sub: "Élégance & longévité" },
  { id: "zinc", label: "Zinc", sub: "Esprit haussmannien" },
  { id: "plat", label: "Toit plat / terrasse", sub: "Étanchéité moderne" },
];
const PROJECT_TYPES = [
  { id: "renovation", label: "Rénovation complète", sub: "Toute la couverture" },
  { id: "reparation", label: "Réparation / fuite", sub: "Dégâts & urgences" },
  { id: "isolation", label: "Isolation & étanchéité", sub: "Combles & terrasses" },
  { id: "neuf", label: "Toiture neuve", sub: "Construction" },
];
const URGENCY = [
  { id: "standard", label: "Standard" },
  { id: "rapide", label: "Sous 7 jours" },
  { id: "urgence", label: "Urgence 48 h" },
];

const PROMISES = [
  { icon: Phone, title: COMPANY.phone, text: "7 jours sur 7 pour les urgences" },
  { icon: CalendarCheck, title: "Visite technique offerte", text: "Partout en Gironde" },
  { icon: ShieldCheck, title: "Garantie décennale", text: "Tous nos chantiers couverts 10 ans" },
];

const inputCls =
  "w-full rounded-md border border-white/10 bg-[#0D0F12] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 transition-colors focus:border-terra focus:outline-none";

function Option({ active, onClick, label, sub, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`rounded-md border p-4 text-left transition-colors duration-200 ${
        active ? "border-terra bg-terra/10" : "border-white/10 bg-white/[0.02] hover:border-white/25"
      }`}
    >
      <span className="block text-sm font-semibold text-slate-100">{label}</span>
      {sub && <span className="mt-1 block text-xs text-slate-400">{sub}</span>}
    </button>
  );
}

export default function DevisForm() {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    roof_type: "tuile",
    project_type: "renovation",
    urgency: "standard",
    surface: 120,
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (form.name.trim().length < 2) return toast.error("Veuillez indiquer votre nom");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return toast.error("Adresse email invalide");
    if (form.phone.replace(/\D/g, "").length < 8) return toast.error("Numéro de téléphone invalide");
    setLoading(true);
    try {
      await axios.post(`${API}/devis`, form);
      setDone(true);
      toast.success("Demande de devis envoyée", {
        description: "Un artisan vous rappelle sous 24 h ouvrées.",
      });
    } catch (e) {
      toast.error("Échec de l’envoi", { description: "Réessayez ou appelez-nous directement." });
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setDone(false);
    setStep(0);
    setForm({ roof_type: "tuile", project_type: "renovation", urgency: "standard", surface: 120, name: "", email: "", phone: "", message: "" });
  };

  const recap = [
    ["Toiture", ROOF_TYPES.find((r) => r.id === form.roof_type).label],
    ["Projet", PROJECT_TYPES.find((p) => p.id === form.project_type).label],
    ["Surface", `${form.surface} m²`],
    ["Délai", URGENCY.find((u) => u.id === form.urgency).label],
  ];

  return (
    <section id="devis" data-testid="devis-section" className="relative border-t border-white/5 py-28 md:py-36">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeading
          eyebrow="Devis gratuit"
          title="Votre devis gratuit en moins d’une minute"
          copy="Réponse sous 24 h ouvrées. Visite technique offerte partout en Gironde."
        />

        <div className="mt-16 grid gap-8 lg:grid-cols-[1fr_1.25fr]">
          <div className="lg:sticky lg:top-28 lg:self-start">
            <div className="border border-white/5 bg-[#14171D] p-8">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-terra">Sans engagement</p>
              <ul className="mt-7 space-y-5">
                {PROMISES.map((p) => (
                  <li key={p.title} className="flex items-center gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-terra/30 bg-terra/10 text-terra">
                      <p.icon className="h-4 w-4" />
                    </span>
                    <span>
                      <span className="block text-sm font-semibold text-slate-100">{p.title}</span>
                      <span className="block text-xs text-slate-500">{p.text}</span>
                    </span>
                  </li>
                ))}
              </ul>
              <dl className="mt-8 space-y-3 border-t border-white/5 pt-6">
                {recap.map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-sm">
                    <dt className="text-slate-500">{k}</dt>
                    <dd className="font-medium text-slate-200">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>

          <div className="border border-white/5 bg-[#14171D] p-6 md:p-10">
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease }}
                data-testid="devis-success"
                className="flex flex-col items-center py-16 text-center"
              >
                <CheckCircle2 className="h-14 w-14 text-terra" />
                <h3 className="mt-6 font-serif text-3xl text-white">Demande envoyée</h3>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-400">
                  Merci {form.name.split(" ")[0]}. Un artisan couvreur vous rappelle sous 24 h ouvrées au {form.phone}.
                </p>
                <button
                  onClick={reset}
                  data-testid="devis-reset-button"
                  className="mt-8 rounded-full border border-white/15 px-6 py-3 text-sm font-semibold text-slate-200 transition-colors hover:border-terra hover:text-terra"
                >
                  Nouvelle demande
                </button>
              </motion.div>
            ) : (
              <>
                <div className="mb-8">
                  <div className="flex items-center justify-between">
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-400">
                      Étape {step + 1} / 3
                    </p>
                    <p className="font-mono text-xs text-terra">
                      {["Votre toiture", "Votre projet", "Vos coordonnées"][step]}
                    </p>
                  </div>
                  <div className="mt-3 h-1 w-full bg-white/5">
                    <motion.div
                      animate={{ width: `${((step + 1) / 3) * 100}%` }}
                      transition={{ duration: 0.4, ease }}
                      className="h-full bg-terra"
                    />
                  </div>
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, x: 32 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -32 }}
                    transition={{ duration: 0.35, ease }}
                  >
                    {step === 0 && (
                      <div className="grid gap-3 sm:grid-cols-2">
                        {ROOF_TYPES.map((r) => (
                          <Option
                            key={r.id}
                            testid={`devis-roof-${r.id}`}
                            active={form.roof_type === r.id}
                            onClick={() => set("roof_type", r.id)}
                            label={r.label}
                            sub={r.sub}
                          />
                        ))}
                      </div>
                    )}

                    {step === 1 && (
                      <div className="space-y-6">
                        <div className="grid gap-3 sm:grid-cols-2">
                          {PROJECT_TYPES.map((p) => (
                            <Option
                              key={p.id}
                              testid={`devis-project-${p.id}`}
                              active={form.project_type === p.id}
                              onClick={() => set("project_type", p.id)}
                              label={p.label}
                              sub={p.sub}
                            />
                          ))}
                        </div>
                        <div>
                          <label className="mb-2 block text-xs font-semibold uppercase tracking-widest text-slate-400">
                            Surface estimée : <span className="text-terra">{form.surface} m²</span>
                          </label>
                          <input
                            type="range"
                            min="10"
                            max="400"
                            step="5"
                            value={form.surface}
                            data-testid="devis-surface-slider"
                            onChange={(e) => set("surface", Number(e.target.value))}
                            className="w-full accent-[#D96B43]"
                          />
                        </div>
                        <div>
                          <label className="mb-2 block text-xs font-semibold uppercase tracking-widest text-slate-400">
                            Délai souhaité
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {URGENCY.map((u) => (
                              <button
                                key={u.id}
                                type="button"
                                data-testid={`devis-urgency-${u.id}`}
                                onClick={() => set("urgency", u.id)}
                                className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors duration-200 ${
                                  form.urgency === u.id
                                    ? "border-terra bg-terra text-white"
                                    : "border-white/10 text-slate-300 hover:border-white/30"
                                }`}
                              >
                                {u.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {step === 2 && (
                      <div className="space-y-4">
                        <div className="grid gap-4 sm:grid-cols-2">
                          <input
                            data-testid="devis-input-name"
                            value={form.name}
                            onChange={(e) => set("name", e.target.value)}
                            placeholder="Nom et prénom *"
                            className={inputCls}
                          />
                          <input
                            data-testid="devis-input-phone"
                            value={form.phone}
                            onChange={(e) => set("phone", e.target.value)}
                            placeholder="Téléphone *"
                            type="tel"
                            className={inputCls}
                          />
                        </div>
                        <input
                          data-testid="devis-input-email"
                          value={form.email}
                          onChange={(e) => set("email", e.target.value)}
                          placeholder="Adresse email *"
                          type="email"
                          className={inputCls}
                        />
                        <textarea
                          data-testid="devis-input-message"
                          value={form.message}
                          onChange={(e) => set("message", e.target.value)}
                          placeholder="Décrivez votre projet (fuites, état de la toiture, accès…)"
                          rows={4}
                          className={`${inputCls} resize-none`}
                        />
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>

                <div className="mt-8 flex items-center justify-between">
                  <button
                    type="button"
                    data-testid="devis-back-button"
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    disabled={step === 0}
                    className="flex items-center gap-2 text-sm font-semibold text-slate-400 transition-colors hover:text-white disabled:opacity-30"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Retour
                  </button>
                  {step < 2 ? (
                    <button
                      type="button"
                      data-testid="devis-next-button"
                      onClick={() => setStep((s) => s + 1)}
                      className="group flex items-center gap-3 rounded-full bg-terra px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-terra-hover"
                    >
                      Continuer
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid="devis-form-submit-button"
                      onClick={submit}
                      disabled={loading}
                      className="flex items-center gap-3 rounded-full bg-terra px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-terra-hover disabled:opacity-60"
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      {loading ? "Envoi en cours…" : "Envoyer ma demande"}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
